import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Users, Plus, Trash2, Pencil, Calendar, Clock, BarChart3,
  MessageSquare, FolderOpen, UserCheck, UserX, Briefcase,
  Play, Pause, Square, Download, Search, Filter, X,
  ChevronLeft, ChevronRight, MapPin, Phone, Mail
} from "lucide-react";
import { FileText, Pen, Car } from "lucide-react";
import { EinsatzberichtView, UnterschriftView, FahrzeugeView } from "./WorkforceExtras";
import { RecurringView, BenachrichtigungenView } from "./RecurringNotify";
import { DokumenteView } from "./DokumenteView";
import { useOrgCurrency } from "@/lib/currency-context";
import { Bell, Repeat } from "lucide-react";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtH = (h) => `${h.toFixed(1)}h`;

const STATUS_COLORS = {
  verfuegbar: "bg-green-100 text-green-800",
  urlaub: "bg-blue-100 text-blue-800",
  krank: "bg-red-100 text-red-800",
  einsatz: "bg-yellow-100 text-yellow-800",
};
const STATUS_LABELS = {
  verfuegbar: "Verfügbar",
  urlaub: "Im Urlaub",
  krank: "Krank",
  einsatz: "Im Einsatz",
};

/* ========== WORKFORCE TAB HAUPTKOMPONENTE ========== */
export const WorkforceTab = () => {
  const { currency, formatMoney: fmt } = useOrgCurrency();
  const [view, setView] = useState("mitarbeiter");
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [auftraege, setAuftraege] = useState([]);
  const [projekte, setProjekte] = useState([]);
  const [zeiteintraege, setZeiteintraege] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Forms
  const [showMaForm, setShowMaForm] = useState(false);
  const [editMa, setEditMa] = useState(null);
  const [showAbsForm, setShowAbsForm] = useState(false);
  const [showAuftragForm, setShowAuftragForm] = useState(false);
  const [showProjektForm, setShowProjektForm] = useState(false);

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatChannel, setChatChannel] = useState(null);
  const [chatText, setChatText] = useState("");
  const [channels, setChannels] = useState([]);

  // Analytics
  const [analytics, setAnalytics] = useState(null);

  // Kalender
  const [kalenderMonat, setKalenderMonat] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  // Load data
  const loadMitarbeiter = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/workforce/mitarbeiter`);
      setMitarbeiter(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  const loadAbsences = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/workforce/abwesenheiten`);
      setAbsences(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  const loadAuftraege = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/auftraege`);
      setAuftraege(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  const loadProjekte = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/projekte`);
      setProjekte(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customers`);
      setCustomers(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  const loadChannels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/teamchat/channels`);
      setChannels(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    loadMitarbeiter();
    loadCustomers();
  }, []);

  useEffect(() => {
    if (view === "mitarbeiter") loadMitarbeiter();
    if (view === "kalender") loadAbsences();
    if (view === "einsaetze") { loadAuftraege(); loadMitarbeiter(); }
    if (view === "berichte" || view === "unterschrift" || view === "fahrzeuge" || view === "wiederkehrend" || view === "dokumente") { loadAuftraege(); loadProjekte(); }
    if (view === "benachrichtigungen") { loadMitarbeiter(); }
    if (view === "projekte") loadProjekte();
    if (view === "chat") loadChannels();
  }, [view]);

  // Sub-tab button
  const btn = (id, label, Icon) => (
    <button
      onClick={() => setView(id)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${
        view === id ? "bg-[var(--brand-primary)] text-white" : "text-[var(--brand-muted)] hover:text-[var(--brand-ink)]"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div>
      {/* Sub-Navigation */}
      <div className="flex flex-wrap gap-1 rounded-lg border border-[var(--brand-border)] bg-white p-1 mb-4">
        {btn("mitarbeiter", "Mitarbeiter", Users)}
        {btn("kalender", "Kalender", Calendar)}
        {btn("einsaetze", "Einsätze", MapPin)}
        {btn("projekte", "Projekte", FolderOpen)}
        {btn("zeiterfassung", "Zeiterfassung", Clock)}
        {btn("auswertungen", "Auswertungen", BarChart3)}
        {btn("chat", "Teamchat", MessageSquare)}
        {btn("berichte", "Berichte", FileText)}
        {btn("unterschrift", "Unterschrift", Pen)}
        {btn("fahrzeuge", "Fahrzeuge", Car)}
        {btn("wiederkehrend", "Wiederkehrend", Repeat)}
        {btn("benachrichtigungen", "Meldungen", Bell)}
        {btn("dokumente", "Dokumente", FolderOpen)}
      </div>

      {/* Content */}
      {view === "mitarbeiter" && (
        <MitarbeiterView
          mitarbeiter={mitarbeiter}
          loading={loading}
          showForm={showMaForm}
          setShowForm={setShowMaForm}
          editMa={editMa}
          setEditMa={setEditMa}
          reload={loadMitarbeiter}
        />
      )}
      {view === "kalender" && (
        <KalenderView
          monat={kalenderMonat}
          setMonat={setKalenderMonat}
          mitarbeiter={mitarbeiter}
          absences={absences}
          reload={() => { loadAbsences(); loadMitarbeiter(); }}
          showForm={showAbsForm}
          setShowForm={setShowAbsForm}
        />
      )}
      {view === "einsaetze" && (
        <EinsaetzeView
          auftraege={auftraege}
          mitarbeiter={mitarbeiter}
          customers={customers}
          projekte={projekte}
          showForm={showAuftragForm}
          setShowForm={setShowAuftragForm}
          reload={() => { loadAuftraege(); loadMitarbeiter(); }}
        />
      )}
      {view === "projekte" && (
        <ProjekteView
          projekte={projekte}
          mitarbeiter={mitarbeiter}
          customers={customers}
          showForm={showProjektForm}
          setShowForm={setShowProjektForm}
          reload={loadProjekte}
        />
      )}
      {view === "zeiterfassung" && (
        <ZeiterfassungView mitarbeiter={mitarbeiter} projekte={projekte} auftraege={auftraege} />
      )}
      {view === "auswertungen" && <AuswertungenView />}
      {view === "chat" && (
        <TeamchatView
          channels={channels}
          chatChannel={chatChannel}
          setChatChannel={setChatChannel}
          messages={chatMessages}
          setMessages={setChatMessages}
          chatText={chatText}
          setChatText={setChatText}
          reload={loadChannels}
        />
      )}
      {view === "berichte" && <EinsatzberichtView auftraege={auftraege} />}
      {view === "unterschrift" && <UnterschriftView auftraege={auftraege} />}
      {view === "fahrzeuge" && <FahrzeugeView auftraege={auftraege} />}
      {view === "wiederkehrend" && <RecurringView auftraege={auftraege} />}
      {view === "benachrichtigungen" && <BenachrichtigungenView mitarbeiter={mitarbeiter} />}
      {view === "dokumente" && <DokumenteView projekte={projekte} auftraege={auftraege} />}
    </div>
  );
};

/* ========== MITARBEITER VIEW ========== */
const MitarbeiterView = ({ mitarbeiter, showForm, setShowForm, editMa, setEditMa, reload }) => {
  const [form, setForm] = useState({ vorname: "", nachname: "", email: "", telefon: "", rolle: "", qualifikation: "", stunden_pro_woche: 40, stundensatz: 0 });

  const handleSave = async () => {
    try {
      if (editMa) {
        await axios.put(`${API}/workforce/mitarbeiter/${editMa.id}`, form);
        toast.success("Mitarbeiter aktualisiert");
      } else {
        await axios.post(`${API}/workforce/mitarbeiter`, form);
        toast.success("Mitarbeiter angelegt");
      }
      setShowForm(false);
      setEditMa(null);
      setForm({ vorname: "", nachname: "", email: "", telefon: "", rolle: "", qualifikation: "", stunden_pro_woche: 40, stundensatz: 0 });
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Mitarbeiter wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/workforce/mitarbeiter/${id}`);
      toast.success("Gelöscht");
      reload();
    } catch (err) { toast.error("Fehler beim Löschen"); }
  };

  const startEdit = (ma) => {
    setForm({ vorname: ma.vorname, nachname: ma.nachname, email: ma.email, telefon: ma.telefon, rolle: ma.rolle, qualifikation: ma.qualifikation, stunden_pro_woche: ma.stunden_pro_woche, stundensatz: ma.stundensatz });
    setEditMa(ma);
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-[var(--brand-ink)]">Mitarbeiter ({mitarbeiter.length})</h2>
        <Button onClick={() => { setEditMa(null); setForm({ vorname: "", nachname: "", email: "", telefon: "", rolle: "", qualifikation: "", stunden_pro_woche: 40, stundensatz: 0 }); setShowForm(true); }} size="sm">
          <Plus className="w-4 h-4 mr-1" /> Neu
        </Button>
      </div>

      {showForm && (
        <Card className="p-4 mb-4 space-y-3">
          <h3 className="font-medium">{editMa ? "Mitarbeiter bearbeiten" : "Neuer Mitarbeiter"}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Vorname *</Label><Input value={form.vorname} onChange={e => setForm({ ...form, vorname: e.target.value })} /></div>
            <div><Label>Nachname *</Label><Input value={form.nachname} onChange={e => setForm({ ...form, nachname: e.target.value })} /></div>
            <div><Label>E-Mail</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Telefon</Label><Input value={form.telefon} onChange={e => setForm({ ...form, telefon: e.target.value })} /></div>
            <div><Label>Rolle</Label><Input value={form.rolle} onChange={e => setForm({ ...form, rolle: e.target.value })} placeholder="z.B. Projektleiter, Techniker" /></div>
            <div><Label>Qualifikation</Label><Input value={form.qualifikation} onChange={e => setForm({ ...form, qualifikation: e.target.value })} /></div>
            <div><Label>Stunden/Woche</Label><Input type="number" value={form.stunden_pro_woche} onChange={e => setForm({ ...form, stunden_pro_woche: parseFloat(e.target.value) })} /></div>
            <div><Label>Stundensatz ({currency})</Label><Input type="number" value={form.stundensatz} onChange={e => setForm({ ...form, stundensatz: parseFloat(e.target.value) })} /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} size="sm">{editMa ? "Speichern" : "Anlegen"}</Button>
            <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setEditMa(null); }}>Abbrechen</Button>
          </div>
        </Card>
      )}

      <div className="grid gap-3">
        {mitarbeiter.map(ma => (
          <Card key={ma.id} className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--brand-primary)] text-white flex items-center justify-center font-semibold text-sm">
                {ma.vorname?.[0]}{ma.nachname?.[0]}
              </div>
              <div>
                <p className="font-medium text-[var(--brand-ink)]">{ma.vorname} {ma.nachname}</p>
                <p className="text-xs text-[var(--brand-muted)]">{ma.rolle}{ma.qualifikation ? ` • ${ma.qualifikation}` : ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ma.status] || "bg-gray-100"}`}>
                {STATUS_LABELS[ma.status] || ma.status}
              </span>
              <Button variant="ghost" size="sm" onClick={() => startEdit(ma)}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(ma.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
            </div>
          </Card>
        ))}
        {mitarbeiter.length === 0 && <p className="text-[var(--brand-muted)] text-sm text-center py-8">Noch keine Mitarbeiter angelegt.</p>}
      </div>
    </div>
  );
};

/* ========== KALENDER VIEW ========== */
const KalenderView = ({ monat, setMonat, mitarbeiter, absences, reload, showForm, setShowForm }) => {
  const [form, setForm] = useState({ mitarbeiter_id: "", typ: "urlaub", von: "", bis: "", grund: "", status: "genehmigt" });

  const handleSave = async () => {
    try {
      await axios.post(`${API}/workforce/abwesenheiten`, form);
      toast.success("Abwesenheit eingetragen");
      setShowForm(false);
      setForm({ mitarbeiter_id: "", typ: "urlaub", von: "", bis: "", grund: "", status: "genehmigt" });
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/workforce/abwesenheiten/${id}`, { status: "genehmigt" });
      toast.success("Genehmigt");
      reload();
    } catch (err) { toast.error("Fehler"); }
  };

  const handleReject = async (id) => {
    try {
      await axios.put(`${API}/workforce/abwesenheiten/${id}`, { status: "abgelehnt" });
      toast.success("Abgelehnt");
      reload();
    } catch (err) { toast.error("Fehler"); }
  };

  const prevMonth = () => {
    const [y, m] = monat.split("-").map(Number);
    const d = new Date(y, m - 2, 1);
    setMonat(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const nextMonth = () => {
    const [y, m] = monat.split("-").map(Number);
    const d = new Date(y, m, 1);
    setMonat(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const monatLabel = () => {
    const [y, m] = monat.split("-");
    const months = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
          <h2 className="text-lg font-semibold text-[var(--brand-ink)]">{monatLabel()}</h2>
          <Button variant="outline" size="sm" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <Button onClick={() => setShowForm(true)} size="sm"><Plus className="w-4 h-4 mr-1" /> Abwesenheit</Button>
      </div>

      {showForm && (
        <Card className="p-4 mb-4 space-y-3">
          <h3 className="font-medium">Abwesenheit eintragen</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Mitarbeiter *</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.mitarbeiter_id} onChange={e => setForm({ ...form, mitarbeiter_id: e.target.value })}>
                <option value="">Bitte wählen</option>
                {mitarbeiter.map(ma => <option key={ma.id} value={ma.id}>{ma.vorname} {ma.nachname}</option>)}
              </select>
            </div>
            <div>
              <Label>Typ *</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.typ} onChange={e => setForm({ ...form, typ: e.target.value })}>
                <option value="urlaub">Urlaub</option>
                <option value="krank">Krankmeldung</option>
                <option value="sonstiges">Sonstiges</option>
              </select>
            </div>
            <div><Label>Von *</Label><Input type="date" value={form.von} onChange={e => setForm({ ...form, von: e.target.value })} /></div>
            <div><Label>Bis *</Label><Input type="date" value={form.bis} onChange={e => setForm({ ...form, bis: e.target.value })} /></div>
            <div className="col-span-2"><Label>Grund</Label><Input value={form.grund} onChange={e => setForm({ ...form, grund: e.target.value })} /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} size="sm">Eintragen</Button>
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Abbrechen</Button>
          </div>
        </Card>
      )}

      {/* Abwesenheiten-Liste */}
      <div className="space-y-2">
        {absences.filter(a => a.von <= `${monat}-31` && a.bis >= `${monat}-01`).map(a => {
          const ma = mitarbeiter.find(m => m.id === a.mitarbeiter_id);
          return (
            <Card key={a.id} className="p-3 flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">{ma ? `${ma.vorname} ${ma.nachname}` : "?"}</p>
                <p className="text-xs text-[var(--brand-muted)]">
                  {a.typ === "urlaub" ? "🏖️ Urlaub" : a.typ === "krank" ? "🤒 Krank" : "📋 Sonstiges"} • {a.von} – {a.bis}
                  {a.grund ? ` • ${a.grund}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <span className={`px-2 py-0.5 rounded text-xs ${a.status === "genehmigt" ? "bg-green-100 text-green-700" : a.status === "abgelehnt" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>
                  {a.status}
                </span>
                {a.status === "beantragt" && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => handleApprove(a.id)} className="text-green-600 text-xs">✓</Button>
                    <Button variant="ghost" size="sm" onClick={() => handleReject(a.id)} className="text-red-600 text-xs">✗</Button>
                  </>
                )}
              </div>
            </Card>
          );
        })}
        {absences.length === 0 && <p className="text-[var(--brand-muted)] text-sm text-center py-8">Keine Abwesenheiten in diesem Monat.</p>}
      </div>
    </div>
  );
};

/* ========== EINSÄTZE VIEW ========== */
const EinsaetzeView = ({ auftraege, mitarbeiter, customers, projekte, showForm, setShowForm, reload }) => {
  const [form, setForm] = useState({ titel: "", kunde_id: "", einsatzort: "", datum: "", zeit_von: "", zeit_bis: "", mitarbeiter_ids: [], beschreibung: "", projekt_id: "" });

  const handleSave = async () => {
    try {
      await axios.post(`${API}/projects/auftraege`, form);
      toast.success("Auftrag angelegt");
      setShowForm(false);
      setForm({ titel: "", kunde_id: "", einsatzort: "", datum: "", zeit_von: "", zeit_bis: "", mitarbeiter_ids: [], beschreibung: "", projekt_id: "" });
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const toggleMa = (id) => {
    setForm(f => ({
      ...f,
      mitarbeiter_ids: f.mitarbeiter_ids.includes(id)
        ? f.mitarbeiter_ids.filter(x => x !== id)
        : [...f.mitarbeiter_ids, id]
    }));
  };

  const handleDelete = async (id) => {
    if (!confirm("Auftrag wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/projects/auftraege/${id}`);
      toast.success("Gelöscht");
      reload();
    } catch (err) { toast.error("Fehler"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-[var(--brand-ink)]">Einsätze & Aufträge ({auftraege.length})</h2>
        <Button onClick={() => setShowForm(true)} size="sm"><Plus className="w-4 h-4 mr-1" /> Neuer Auftrag</Button>
      </div>

      {showForm && (
        <Card className="p-4 mb-4 space-y-3">
          <h3 className="font-medium">Neuer Auftrag</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Titel *</Label><Input value={form.titel} onChange={e => setForm({ ...form, titel: e.target.value })} placeholder="z.B. Wartung Heizungsanlage" /></div>
            <div>
              <Label>Kunde (aus CRM)</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.kunde_id} onChange={e => setForm({ ...form, kunde_id: e.target.value })}>
                <option value="">— Kein Kunde —</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.kunde_firma || c.kunde_name}</option>)}
              </select>
            </div>
            <div><Label>Einsatzort</Label><Input value={form.einsatzort} onChange={e => setForm({ ...form, einsatzort: e.target.value })} placeholder="Adresse" /></div>
            <div><Label>Datum *</Label><Input type="date" value={form.datum} onChange={e => setForm({ ...form, datum: e.target.value })} /></div>
            <div><Label>Von (Uhrzeit)</Label><Input type="time" value={form.zeit_von} onChange={e => setForm({ ...form, zeit_von: e.target.value })} /></div>
            <div><Label>Bis (Uhrzeit)</Label><Input type="time" value={form.zeit_bis} onChange={e => setForm({ ...form, zeit_bis: e.target.value })} /></div>
            <div>
              <Label>Projekt</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.projekt_id} onChange={e => setForm({ ...form, projekt_id: e.target.value })}>
                <option value="">— Kein Projekt —</option>
                {projekte.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div><Label>Beschreibung</Label><Input value={form.beschreibung} onChange={e => setForm({ ...form, beschreibung: e.target.value })} /></div>
          </div>
          <div>
            <Label className="mb-1 block">Mitarbeiter zuweisen (nur verfügbare)</Label>
            <div className="flex flex-wrap gap-2">
              {mitarbeiter.filter(m => m.status === "verfuegbar").map(ma => (
                <button
                  key={ma.id}
                  onClick={() => toggleMa(ma.id)}
                  className={`px-2 py-1 rounded text-xs border transition ${form.mitarbeiter_ids.includes(ma.id) ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "border-gray-300 hover:border-[var(--brand-primary)]"}`}
                >
                  {ma.vorname} {ma.nachname}
                </button>
              ))}
              {mitarbeiter.filter(m => m.status === "verfuegbar").length === 0 && <span className="text-xs text-[var(--brand-muted)]">Keine verfügbaren Mitarbeiter</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} size="sm">Auftrag anlegen</Button>
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Abbrechen</Button>
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {auftraege.map(a => (
          <Card key={a.id} className="p-3">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium text-[var(--brand-ink)]">{a.titel}</p>
                <p className="text-xs text-[var(--brand-muted)]">
                  📅 {a.datum} {a.zeit_von && `• ${a.zeit_von}–${a.zeit_bis}`}
                  {a.kunde_name && ` • 👤 ${a.kunde_name}`}
                  {a.einsatzort && ` • 📍 ${a.einsatzort}`}
                </p>
                {a.mitarbeiter_ids?.length > 0 && (
                  <p className="text-xs mt-1">
                    👥 {a.mitarbeiter_ids.length} Mitarbeiter zugewiesen
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className={`px-2 py-0.5 rounded text-xs ${a.status === "abgeschlossen" ? "bg-green-100 text-green-700" : a.status === "aktiv" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"}`}>
                  {a.status}
                </span>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
              </div>
            </div>
          </Card>
        ))}
        {auftraege.length === 0 && <p className="text-[var(--brand-muted)] text-sm text-center py-8">Noch keine Aufträge angelegt.</p>}
      </div>
    </div>
  );
};

/* ========== PROJEKTE VIEW ========== */
const ProjekteView = ({ projekte, mitarbeiter, customers, showForm, setShowForm, reload }) => {
  const [form, setForm] = useState({ name: "", beschreibung: "", kunde_id: "", status: "aktiv", mitarbeiter_ids: [], budget_stunden: 0, budget_euro: 0 });

  const handleSave = async () => {
    try {
      await axios.post(`${API}/projects/projekte`, form);
      toast.success("Projekt angelegt");
      setShowForm(false);
      setForm({ name: "", beschreibung: "", kunde_id: "", status: "aktiv", mitarbeiter_ids: [], budget_stunden: 0, budget_euro: 0 });
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Projekt wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/projects/projekte/${id}`);
      toast.success("Gelöscht");
      reload();
    } catch (err) { toast.error("Fehler"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-[var(--brand-ink)]">Projekte ({projekte.length})</h2>
        <Button onClick={() => setShowForm(true)} size="sm"><Plus className="w-4 h-4 mr-1" /> Neues Projekt</Button>
      </div>

      {showForm && (
        <Card className="p-4 mb-4 space-y-3">
          <h3 className="font-medium">Neues Projekt</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div>
              <Label>Kunde (aus CRM)</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.kunde_id} onChange={e => setForm({ ...form, kunde_id: e.target.value })}>
                <option value="">— Kein Kunde —</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.kunde_firma || c.kunde_name}</option>)}
              </select>
            </div>
            <div><Label>Budget (Stunden)</Label><Input type="number" value={form.budget_stunden} onChange={e => setForm({ ...form, budget_stunden: parseFloat(e.target.value) })} /></div>
            <div><Label>Budget ({currency})</Label><Input type="number" value={form.budget_euro} onChange={e => setForm({ ...form, budget_euro: parseFloat(e.target.value) })} /></div>
            <div className="col-span-2"><Label>Beschreibung</Label><Textarea value={form.beschreibung} onChange={e => setForm({ ...form, beschreibung: e.target.value })} /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} size="sm">Projekt anlegen</Button>
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Abbrechen</Button>
          </div>
        </Card>
      )}

      <div className="grid gap-3">
        {projekte.map(p => (
          <Card key={p.id} className="p-3">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium text-[var(--brand-ink)]">{p.name}</p>
                <p className="text-xs text-[var(--brand-muted)]">
                  {p.kunde_name && `👤 ${p.kunde_name} • `}
                  {p.budget_stunden > 0 && `⏱️ ${p.budget_stunden}h Budget • `}
                  {p.budget_euro > 0 && `💰 ${fmt(p.budget_euro)} Budget`}
                </p>
                {p.beschreibung && <p className="text-xs mt-1 text-gray-600">{p.beschreibung}</p>}
              </div>
              <div className="flex items-center gap-1">
                <span className={`px-2 py-0.5 rounded text-xs ${p.status === "aktiv" ? "bg-green-100 text-green-700" : p.status === "pausiert" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-700"}`}>
                  {p.status}
                </span>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
              </div>
            </div>
          </Card>
        ))}
        {projekte.length === 0 && <p className="text-[var(--brand-muted)] text-sm text-center py-8">Noch keine Projekte angelegt.</p>}
      </div>
    </div>
  );
};

/* ========== ZEITERFASSUNG VIEW ========== */
const ZeiterfassungView = ({ mitarbeiter, projekte, auftraege }) => {
  const [selectedMa, setSelectedMa] = useState("");
  const [selectedProjekt, setSelectedProjekt] = useState("");
  const [aktiv, setAktiv] = useState(null);
  const [eintraege, setEintraege] = useState([]);
  const [gpsOptIn, setGpsOptIn] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);

  const captureGps = async () => {
    if (!gpsOptIn) return null;
    if (!navigator.geolocation) throw new Error("Dieser Browser unterstützt keine Standortfreigabe");
    setGpsBusy(true);
    try {
      const position = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }));
      return { latitude: position.coords.latitude, longitude: position.coords.longitude, accuracy_m: position.coords.accuracy, captured_at: new Date().toISOString(), consent_confirmed: true };
    } finally { setGpsBusy(false); }
  };

  const loadAktiv = useCallback(async () => {
    if (!selectedMa) return;
    try {
      const res = await axios.get(`${API}/zeiterfassung/aktiv?mitarbeiter_id=${selectedMa}`);
      setAktiv(res.data?.id ? res.data : null);
    } catch { setAktiv(null); }
  }, [selectedMa]);

  const loadEintraege = useCallback(async () => {
    if (!selectedMa) return;
    try {
      const res = await axios.get(`${API}/zeiterfassung/eintraege?mitarbeiter_id=${selectedMa}`);
      setEintraege(res.data || []);
    } catch { setEintraege([]); }
  }, [selectedMa]);

  useEffect(() => { loadAktiv(); loadEintraege(); }, [selectedMa]);

  const handleStart = async () => {
    try {
      let gps_start = null;
      if (gpsOptIn) {
        try { gps_start = await captureGps(); }
        catch { toast.warning("Standort konnte nicht ermittelt werden. Die Zeiterfassung startet ohne Standortpunkt."); }
      }
      const res = await axios.post(`${API}/zeiterfassung/start`, { mitarbeiter_id: selectedMa, projekt_id: selectedProjekt, gps_start });
      setAktiv(res.data);
      toast.success(gps_start ? "Zeiterfassung mit Standortpunkt gestartet" : "Zeiterfassung gestartet");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleAction = async (aktion) => {
    if (!aktiv) return;
    try {
      let gps_end = null;
      if (aktion === "stop" && gpsOptIn) {
        try { gps_end = await captureGps(); }
        catch { toast.warning("Standort konnte nicht ermittelt werden. Feierabend wird ohne Standortpunkt gespeichert."); }
      }
      const res = await axios.post(`${API}/zeiterfassung/aktion/${aktiv.id}`, { aktion, gps_end });
      if (aktion === "stop") {
        setAktiv(null);
        toast.success(`Feierabend! ${fmtH(res.data.gesamt_minuten / 60)} gearbeitet`);
        loadEintraege();
      } else {
        setAktiv(res.data);
        toast.success(aktion === "pause" ? "Pause" : "Weiter geht's");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleExportPdf = async () => {
    if (!selectedMa) return;
    const heute = new Date();
    const von = `${heute.getFullYear()}-${String(heute.getMonth() + 1).padStart(2, "0")}-01`;
    const bis = heute.toISOString().split("T")[0];
    try {
      const res = await axios.post(`${API}/zeiterfassung/stundenzettel/pdf`, {
        mitarbeiter_id: selectedMa, von, bis
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Stundenzettel_${von}_${bis}.pdf`;
      a.click();
      toast.success("PDF heruntergeladen");
    } catch (err) { toast.error("PDF-Export fehlgeschlagen"); }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-[var(--brand-ink)] mb-4">Zeiterfassung</h2>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <Label>Mitarbeiter auswählen</Label>
          <select className="w-full border rounded px-2 py-1.5 text-sm" value={selectedMa} onChange={e => setSelectedMa(e.target.value)}>
            <option value="">Bitte wählen</option>
            {mitarbeiter.map(ma => <option key={ma.id} value={ma.id}>{ma.vorname} {ma.nachname}</option>)}
          </select>
        </div>
        <div>
          <Label>Projekt / Auftrag</Label>
          <select className="w-full border rounded px-2 py-1.5 text-sm" value={selectedProjekt} onChange={e => setSelectedProjekt(e.target.value)}>
            <option value="">— Kein Projekt —</option>
            {projekte.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {selectedMa && (
        <>
          <Card className="p-3 mb-4 bg-amber-50 border-amber-200" data-testid="gps-clock-option">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={gpsOptIn} onChange={e => setGpsOptIn(e.target.checked)} className="mt-1" />
              <span><span className="text-sm font-semibold text-[var(--brand-ink)]">Standort einmalig bei Arbeitsbeginn und Feierabend dokumentieren</span><span className="block text-xs text-[var(--brand-muted)] mt-1">Optional. Es wird kein Live-Tracking durchgeführt. Nur wenn du dieses Feld aktivierst und die Browserfreigabe erteilst, speichert die Zeiterfassung jeweils einen Standortpunkt mit Zeit und Genauigkeit.</span></span>
            </label>
          </Card>
          {/* Stoppuhr */}
          <Card className="p-4 mb-4">
            {aktiv ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">⏱️ Läuft seit {new Date(aktiv.start).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</p>
                  <p className="text-xs text-[var(--brand-muted)]">Status: {aktiv.status === "pausiert" ? "⏸️ Pausiert" : "▶️ Aktiv"}</p>
                </div>
                <div className="flex gap-2">
                  {aktiv.status === "aktiv" && (
                    <Button size="sm" variant="outline" onClick={() => handleAction("pause")}><Pause className="w-4 h-4 mr-1" /> Pause</Button>
                  )}
                  {aktiv.status === "pausiert" && (
                    <Button size="sm" variant="outline" onClick={() => handleAction("weiter")}><Play className="w-4 h-4 mr-1" /> Weiter</Button>
                  )}
                  <Button size="sm" onClick={() => handleAction("stop")} disabled={gpsBusy}><Square className="w-4 h-4 mr-1" /> {gpsBusy ? "Standort…" : "Feierabend"}</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-[var(--brand-muted)]">Keine aktive Zeiterfassung</p>
                <Button size="sm" onClick={handleStart} disabled={gpsBusy}><Play className="w-4 h-4 mr-1" /> {gpsBusy ? "Standort…" : "Start"}</Button>
              </div>
            )}
          </Card>

          {/* PDF Export */}
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" onClick={handleExportPdf}><Download className="w-4 h-4 mr-1" /> Stundenzettel PDF</Button>
          </div>

          {/* Letzte Einträge */}
          <h3 className="text-sm font-medium mb-2">Letzte Einträge</h3>
          <div className="space-y-1">
            {eintraege.slice(0, 10).map(e => (
              <div key={e.id} className="flex justify-between items-center py-1.5 px-2 bg-gray-50 rounded text-sm">
                <span>{e.datum}</span>
                <span className="text-[var(--brand-muted)]">{fmtH(e.gesamt_minuten / 60)}</span>
              </div>
            ))}
            {eintraege.length === 0 && <p className="text-[var(--brand-muted)] text-xs text-center py-4">Noch keine Einträge.</p>}
          </div>
        </>
      )}
    </div>
  );
};

/* ========== AUSWERTUNGEN VIEW ========== */
const AuswertungenView = () => {
  const [tab, setTab] = useState("projekte");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = "";
      if (tab === "projekte") url = `${API}/workforce-analytics/stunden-pro-projekt`;
      if (tab === "mitarbeiter") url = `${API}/workforce-analytics/stunden-pro-mitarbeiter`;
      if (tab === "ueberstunden") url = `${API}/workforce-analytics/ueberstunden`;
      if (tab === "abwesenheiten") url = `${API}/workforce-analytics/abwesenheits-uebersicht`;
      const res = await axios.get(url);
      setData(res.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(); }, [tab]);

  return (
    <div>
      <h2 className="text-lg font-semibold text-[var(--brand-ink)] mb-4">Auswertungen</h2>
      <div className="flex gap-2 mb-4">
        {[["projekte", "Pro Projekt"], ["mitarbeiter", "Pro Mitarbeiter"], ["ueberstunden", "Überstunden"], ["abwesenheiten", "Abwesenheiten"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} className={`px-3 py-1 rounded text-sm ${tab === id ? "bg-[var(--brand-primary)] text-white" : "bg-gray-100 hover:bg-gray-200"}`}>{label}</button>
        ))}
      </div>

      {loading ? <p className="text-sm text-[var(--brand-muted)]">Laden...</p> : (
        <div className="overflow-x-auto">
          {tab === "projekte" && (
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="text-left py-2">Projekt</th><th className="text-left">Kunde</th><th className="text-right">Stunden</th><th className="text-right">Budget</th></tr></thead>
              <tbody>
                {data.map((r, i) => (
                  <tr key={i} className="border-b"><td className="py-1.5">{r.projekt_name}</td><td>{r.kunde_name}</td><td className="text-right">{fmtH(r.stunden)}</td><td className="text-right">{r.budget_stunden > 0 ? fmtH(r.budget_stunden) : "—"}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          {tab === "mitarbeiter" && (
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="text-left py-2">Mitarbeiter</th><th className="text-left">Rolle</th><th className="text-right">Stunden</th><th className="text-right">Überstunden</th></tr></thead>
              <tbody>
                {data.map((r, i) => (
                  <tr key={i} className="border-b"><td className="py-1.5">{r.name}</td><td>{r.rolle}</td><td className="text-right">{fmtH(r.stunden)}</td><td className={`text-right ${r.ueberstunden > 0 ? "text-red-600" : "text-green-600"}`}>{fmtH(r.ueberstunden)}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          {tab === "ueberstunden" && (
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="text-left py-2">Mitarbeiter</th><th className="text-right">Ist</th><th className="text-right">Soll</th><th className="text-right">Überstunden</th></tr></thead>
              <tbody>
                {data.map((r, i) => (
                  <tr key={i} className="border-b"><td className="py-1.5">{r.name}</td><td className="text-right">{fmtH(r.ist_stunden)}</td><td className="text-right">{fmtH(r.soll_stunden)}</td><td className={`text-right font-medium ${r.ueberstunden > 0 ? "text-red-600" : "text-green-600"}`}>{r.ueberstunden > 0 ? "+" : ""}{fmtH(r.ueberstunden)}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          {tab === "abwesenheiten" && (
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="text-left py-2">Mitarbeiter</th><th className="text-right">Urlaub</th><th className="text-right">Krank</th><th className="text-right">Sonstiges</th></tr></thead>
              <tbody>
                {data.map((r, i) => (
                  <tr key={i} className="border-b"><td className="py-1.5">{r.name}</td><td className="text-right">{r.urlaub_tage} Tage</td><td className="text-right">{r.krank_tage} Tage</td><td className="text-right">{r.sonstiges_tage} Tage</td></tr>
                ))}
              </tbody>
            </table>
          )}
          {data.length === 0 && <p className="text-[var(--brand-muted)] text-sm text-center py-8">Noch keine Daten vorhanden.</p>}
        </div>
      )}
    </div>
  );
};

/* ========== TEAMCHAT VIEW ========== */
const TeamchatView = ({ channels, chatChannel, setChatChannel, messages, setMessages, chatText, setChatText, reload }) => {
  const loadMessages = useCallback(async () => {
    if (!chatChannel) return;
    try {
      const param = chatChannel.typ === "projekt" ? `projekt_id=${chatChannel.id}` : `auftrag_id=${chatChannel.id}`;
      const res = await axios.get(`${API}/teamchat/nachrichten?${param}`);
      setMessages(res.data || []);
    } catch { setMessages([]); }
  }, [chatChannel]);

  useEffect(() => { loadMessages(); }, [chatChannel]);

  const sendMessage = async () => {
    if (!chatText.trim() || !chatChannel) return;
    try {
      await axios.post(`${API}/teamchat/nachrichten`, {
        projekt_id: chatChannel.typ === "projekt" ? chatChannel.id : "",
        auftrag_id: chatChannel.typ === "auftrag" ? chatChannel.id : "",
        text: chatText,
        absender_name: "Admin",
      });
      setChatText("");
      loadMessages();
    } catch (err) { toast.error("Senden fehlgeschlagen"); }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-[var(--brand-ink)] mb-4">Teamchat</h2>
      <div className="grid grid-cols-3 gap-4" style={{ minHeight: 400 }}>
        {/* Channel-Liste */}
        <div className="border rounded-lg p-2 overflow-y-auto">
          <p className="text-xs font-medium text-[var(--brand-muted)] mb-2 uppercase">Channels</p>
          {channels.map(ch => (
            <button
              key={ch.id}
              onClick={() => setChatChannel(ch)}
              className={`w-full text-left px-2 py-1.5 rounded text-sm mb-1 ${chatChannel?.id === ch.id ? "bg-[var(--brand-primary)] text-white" : "hover:bg-gray-100"}`}
            >
              <span className="font-medium">{ch.typ === "projekt" ? "📁" : "📋"} {ch.name}</span>
              {ch.letzte_nachricht && <p className="text-xs opacity-70 truncate">{ch.letzte_nachricht}</p>}
            </button>
          ))}
          {channels.length === 0 && <p className="text-xs text-[var(--brand-muted)] text-center py-4">Erstelle zuerst ein Projekt oder einen Auftrag.</p>}
        </div>

        {/* Chat-Bereich */}
        <div className="col-span-2 border rounded-lg flex flex-col">
          {chatChannel ? (
            <>
              <div className="p-2 border-b bg-gray-50 rounded-t-lg">
                <p className="text-sm font-medium">{chatChannel.name}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ maxHeight: 320 }}>
                {messages.map(msg => (
                  <div key={msg.id} className="bg-gray-50 rounded px-3 py-2">
                    <p className="text-xs font-medium text-[var(--brand-primary)]">{msg.absender_name}</p>
                    <p className="text-sm">{msg.text}</p>
                    <p className="text-xs text-[var(--brand-muted)] mt-0.5">{new Date(msg.created_at).toLocaleString("de-DE")}</p>
                  </div>
                ))}
                {messages.length === 0 && <p className="text-xs text-center text-[var(--brand-muted)] py-8">Noch keine Nachrichten.</p>}
              </div>
              <div className="p-2 border-t flex gap-2">
                <Input
                  value={chatText}
                  onChange={e => setChatText(e.target.value)}
                  placeholder="Nachricht schreiben..."
                  onKeyDown={e => e.key === "Enter" && sendMessage()}
                  className="flex-1"
                />
                <Button size="sm" onClick={sendMessage}>Senden</Button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-[var(--brand-muted)] text-sm">
              Wähle links einen Channel aus
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
